import { Employee } from 'providers/employees/types'
import { immer } from 'zustand/middleware/immer'

type EmployeesTableStoreState = {
  section: string
  selected: Employee[]
  /**
   * It's important to address that disabled employees are always treated as
   * unselected, but they are not included in this array. Employees are disabled
   * if they are inactive or have a KYC status of DENIED_FINAL ("denied" for the
   * frontend), depending on the props passed to the table.
   *
   * For an example, this may occur when the table is in UNSELECTED mode and
   * disableDeniedKycSelection is true. In this case, the disabled employees are
   * not included in the unselected array, but they should be treated as
   * unselected for matters of selection.
   */
  unselected: Employee[]
  isAllSelected: boolean
  individualAmounts: Record<Employee['idEmployee'], number>
}

type EmployeesTableStoreActions = {
  setIsAllSelected: (newState: boolean, section: string) => void
  resetAllData: () => void
  addInSelected: (employees: Employee[], section: string) => void
  /**
   * Adds or updates selected employees without clearing `unselected`.
   * Used by UNSELECTED-mode tables so selected rows keep fields like `userId`.
   */
  upsertSelected: (employees: Employee[], section: string) => void
  removeFromSelected: (employees: Employee[], section: string) => void
  addInUnSelected: (employees: Employee[], section: string) => void
  removeFromUnSelected: (employees: Employee[], section: string) => void
  resetIndividualAmount: (employee: Employee) => void
  updateIndividualAmount: (employee: Employee, amount: number) => void
}

export type EmployeesTableStore = EmployeesTableStoreState &
  EmployeesTableStoreActions

export const initialState: EmployeesTableStoreState = {
  section: '',
  selected: [],
  unselected: [],
  isAllSelected: false,
  individualAmounts: {}
}

const employeeIsIncluded = (
  employees: Employee[],
  targetEmployee: Employee
) => {
  return employees.some(
    (employee) => employee.idEmployee === targetEmployee.idEmployee
  )
}

const getEmployeesNotIncluded = (
  currentEmployees: Employee[],
  newEmployees: Employee[]
) =>
  newEmployees.filter(
    (employee) => !employeeIsIncluded(currentEmployees, employee)
  )

export const employeesTableStoreCreator = immer<EmployeesTableStore>((set) => ({
  ...initialState,
  setIsAllSelected: (newState, section) => {
    set((state) => {
      state.isAllSelected = newState
      state.selected = []
      state.section = section
    })
  },
  addInSelected: (employees, section) => {
    set((state) => {
      const employeesNotIncludedInSelected = getEmployeesNotIncluded(
        state.selected,
        employees
      )
      state.section = section
      state.selected.push(...employeesNotIncludedInSelected)
      state.isAllSelected = false
      state.unselected = initialState.unselected
    })
  },
  upsertSelected: (employees, section) => {
    set((state) => {
      state.section = section

      for (const employee of employees) {
        const index = state.selected.findIndex(
          (selectedEmployee) =>
            selectedEmployee.idEmployee === employee.idEmployee
        )

        if (index >= 0) {
          state.selected[index] = employee
        } else {
          state.selected.push(employee)
        }
      }
    })
  },
  removeFromSelected: (employees, section) => {
    set((state) => {
      const { selected } = state
      state.section = section
      state.isAllSelected = false
      state.selected = selected.filter(
        (selectedEmployee) => !employeeIsIncluded(employees, selectedEmployee)
      )
      state.unselected = initialState.unselected
    })
  },
  addInUnSelected: (employees, section) => {
    set((state) => {
      const employeesNotIncludedInUnSelected = getEmployeesNotIncluded(
        state.unselected,
        employees
      )
      state.section = section
      state.unselected.push(...employeesNotIncludedInUnSelected)
      state.isAllSelected = false
      state.selected = state.selected.filter(
        (selectedEmployee) => !employeeIsIncluded(employees, selectedEmployee)
      )
    })
  },
  removeFromUnSelected: (employees, section) => {
    set((state) => {
      state.section = section
      state.isAllSelected = false
      state.unselected = state.unselected.filter(
        (unselectedEmployee) =>
          !employeeIsIncluded(employees, unselectedEmployee)
      )
      state.selected.push(...getEmployeesNotIncluded(state.selected, employees))
    })
  },
  updateIndividualAmount: (employee, amount) => {
    set((state) => {
      state.individualAmounts[employee.idEmployee] = amount
    })
  },
  resetIndividualAmount: (employee) => {
    set((state) => {
      const individualAmounts = state.individualAmounts
      delete individualAmounts[employee.idEmployee]

      state.individualAmounts = individualAmounts
    })
  },
  resetAllData: () => {
    set((state) => {
      state.section = initialState.section
      state.selected = initialState.selected
      state.isAllSelected = initialState.isAllSelected
      state.unselected = initialState.unselected
    })
  }
}))
